import { z } from 'zod';
import {
  AUDIT_ACTOR_KINDS,
  INVITE_STATUSES,
  MAIL_KINDS,
  auditTargetTypeSchema,
  clientTypeSchema,
  emailDeliveryStatusSchema,
  guestScopeSchema,
  inviteStatusSchema,
  mailKindSchema,
  managedBySchema,
  memberStatusSchema,
  otpPolicySchema,
  profileFieldEditorSchema,
  profileFieldTypeSchema,
  profileFieldVisibilitySchema,
  roleSchema,
  userKindSchema,
} from '../enums';
import { pushPreviewSchema, readReceiptsSettingSchema } from '../enums/notifications';
import { memberDetailChangeKindSchema, memberDetailChangeStatusSchema } from '../enums/pay';
import {
  emailDigestDefaultHoursSchema,
  emailDomainListSchema,
  importMaxArchiveBytesSchema,
  unfurlBlockedDomainsSchema,
  webhooksManagedBySchema,
  workingDaysSchema,
} from '../settings/registry';
import {
  boundedList,
  commaList,
  deviceNameSchema,
  displayNameSchema,
  emailOutSchema,
  emailSchema,
  hexColorSchema,
  isoDateTimeSchema,
  localDateSchema,
  multiLineText,
  opaqueTokenSchema,
  passwordSchema,
  personNameSchema,
  phoneInputSchema,
  queryBooleanSchema,
  singleLineText,
  timeZoneInputSchema,
  titleSchema,
  uuidSchema,
} from './common';
import { paginated, paginationQuerySchema } from './pagination';

/**
 * Org contracts (PLAN §4 Phase 0): workspace, members and profile, departments, profile field
 * definitions, invites, avatars, the audit log, local signed file URLs and the dev mailbox.
 * The invite-accept endpoints respond with `authSuccessSchema` from `./auth`.
 */

// ================================================================ workspace

export const workspaceSettingsDtoSchema = z.object({
  otpPolicy: otpPolicySchema,
  managersCanInvite: z.boolean(),
  /** [] = any domain; applies to non-guest invites (`auth.allowedEmailDomains`). */
  inviteAllowedDomains: z.array(z.string()),
  /** Phase 2 (D-383): who creates and edits user groups (`messaging.userGroupsManagedBy`). */
  userGroupsManagedBy: managedBySchema,
  /** Who uploads custom emoji (`messaging.emojiUploadBy`). */
  emojiUploadBy: managedBySchema,
  /** Hosts never unfurled, lowercase (`messaging.unfurlBlockedDomains`). */
  unfurlBlockedDomains: z.array(z.string()),
  /** Who manages a channel's incoming webhooks (`integrations.webhooksManagedBy`). */
  webhooksManagedBy: webhooksManagedBySchema,
  /** N hours of the unread-mentions email digest for people who set none. */
  emailDigestDefaultHours: z.number().int(),
  /** Upload ceiling of a Slack export archive, bytes (`import.maxArchiveBytes`). */
  importMaxArchiveBytes: z.number().int(),
  /** Phase 3 (D-550): `messaging.readReceipts` — off, dms (default) or all. */
  readReceipts: readReceiptsSettingSchema,
  /**
   * Phase 3 (D-505, D-572): `notifications.pushPreviewDefault` — what a push shows on the lock
   * screen for everyone who has not chosen their own: full (default), sender_only or none.
   */
  pushPreviewDefault: pushPreviewSchema,
});
export type WorkspaceSettingsDto = z.infer<typeof workspaceSettingsDtoSchema>;

export const workspaceDtoSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  /** Set at creation, read-only. */
  slug: z.string(),
  timezone: z.string(),
  locale: z.literal('en-IN'),
  currency: z.literal('INR'),
  /** ISO weekdays, default [1,2,3,4,5,6] (D-031). */
  workingDays: z.array(z.number().int()),
  accentColor: z.string(),
  /**
   * The workspace's "Hub" system user (D-049, D-1134): the author of the weekly-review DM, the
   * welcome message and every other message the product sends as itself.
   *
   * Clients need this id because the system user is a bare `users` row with NO `members` row, so
   * it is in no directory any client loads. Without it the web app could not tell a product
   * message from a person's and fell back to "Someone" with an avatar initial, and clicking the
   * name opened a profile panel that answered "That person could not be found" — which is how a
   * weekly review from the product looked like a message from a colleague who had been deleted.
   *
   * Optional so an older client reads it as absent rather than failing to parse.
   */
  systemUserId: uuidSchema.optional(),
  /** Workspace logo upload arrives in Phase 2. */
  logo: z.null(),
  createdAt: isoDateTimeSchema,
  /** Non-null only for admin+ viewers. */
  settings: workspaceSettingsDtoSchema.nullable(),
});
export type WorkspaceDto = z.infer<typeof workspaceDtoSchema>;

/** PATCH /workspace — admin+ (`workspace.update`). */
export const updateWorkspaceRequestSchema = z.strictObject({
  name: singleLineText(1, 80).optional(),
  timezone: timeZoneInputSchema.optional(),
  /** Must also reach 4.5:1 against white, else `422 accent_contrast_too_low`. */
  accentColor: hexColorSchema.optional(),
  workingDays: workingDaysSchema.optional(),
  settings: z
    .strictObject({
      otpPolicy: otpPolicySchema.optional(),
      managersCanInvite: z.boolean().optional(),
      inviteAllowedDomains: emailDomainListSchema.optional(),
      userGroupsManagedBy: managedBySchema.optional(),
      emojiUploadBy: managedBySchema.optional(),
      unfurlBlockedDomains: unfurlBlockedDomainsSchema.optional(),
      webhooksManagedBy: webhooksManagedBySchema.optional(),
      emailDigestDefaultHours: emailDigestDefaultHoursSchema.optional(),
      importMaxArchiveBytes: importMaxArchiveBytesSchema.optional(),
      readReceipts: readReceiptsSettingSchema.optional(),
      pushPreviewDefault: pushPreviewSchema.optional(),
    })
    .optional(),
});
export type UpdateWorkspaceRequest = z.infer<typeof updateWorkspaceRequestSchema>;

// ================================================================ members and profile

/**
 * An avatar is named, not linked (D-1105). It used to carry three signed URLs and the moment they
 * expired; it now carries only the identity of the picture, and each client builds the URL for the
 * size it is about to draw against its own API base — which is a relative path in the browser and
 * an absolute origin on the phone, so a URL minted by the server could never have suited both.
 */
export const avatarDtoSchema = z.object({
  fileId: uuidSchema,
  /** Increments on every change; clients cache by (fileId, version, size). */
  version: z.number().int(),
});
export type AvatarDto = z.infer<typeof avatarDtoSchema>;

/** The three stored WebP renderings of an avatar, smallest first. */
export const AVATAR_VARIANTS = ['avatar_48', 'avatar_128', 'avatar_512'] as const;
export type AvatarVariant = (typeof AVATAR_VARIANTS)[number];

/**
 * The smallest stored variant that still looks sharp at `pixels` *device* pixels — so a caller
 * drawing at a CSS size passes `size * 2`. One rule, shared, because the web and the phone drew
 * the same three breakpoints separately before.
 */
export function avatarVariantForSize(pixels: number): AvatarVariant {
  if (pixels <= 48) return 'avatar_48';
  if (pixels <= 128) return 'avatar_128';
  return 'avatar_512';
}

/** text/url/email/phone/date/select → string; multi_select → option ids. */
export const customFieldValueSchema = z.union([z.string(), z.array(z.string()), z.null()]);
export type CustomFieldValue = z.infer<typeof customFieldValueSchema>;

export const memberDtoSchema = z.object({
  userId: uuidSchema,
  /**
   * Phase 2 (D-185): `human`; `bot` for integrations (shown with an APP label); `system` for the
   * workspace's "Hub" user, which `GET /members` lists only when asked for by `ids`.
   */
  kind: userKindSchema,
  /** Null when the viewer is a guest. */
  email: emailOutSchema.nullable(),
  /** From users.full_name. */
  fullName: z.string(),
  displayName: z.string().nullable(),
  title: z.string().nullable(),
  role: roleSchema,
  /** Non-null iff role = guest. */
  guestScope: guestScopeSchema.nullable(),
  status: memberStatusSchema,
  departmentId: uuidSchema.nullable(),
  department: z.object({ id: uuidSchema, name: z.string() }).nullable(),
  /** D-007: stored directly until Phase 5. */
  managerUserId: uuidSchema.nullable(),
  /** E.164; null when the viewer is a guest. */
  phone: z.string().nullable(),
  /** IANA; the workspace timezone when the member has none. */
  timezone: z.string(),
  avatar: avatarDtoSchema.nullable(),
  /** Keyed by profile field id, filtered by field visibility. */
  customFields: z.record(z.string(), customFieldValueSchema),
  joinedAt: isoDateTimeSchema,
  deactivatedAt: isoDateTimeSchema.nullable(),
  /** Non-null only for admin+ viewers. */
  admin: z
    .object({
      invitedByUserId: uuidSchema.nullable(),
      lastActiveAt: isoDateTimeSchema.nullable(),
      /** The payroll employee code and the date of joining (admin+ only, D-930). */
      employeeCode: z.string().nullable().optional(),
      dateOfJoining: localDateSchema.nullable().optional(),
    })
    .nullable(),
});
export type MemberDto = z.infer<typeof memberDtoSchema>;

export const userIdParamsSchema = z.object({ userId: uuidSchema });
export type UserIdParams = z.infer<typeof userIdParamsSchema>;

/** GET /members — `deactivated` and `all` need `members.read_deactivated`. */
export const listMembersQuerySchema = paginationQuerySchema.extend({
  /** Trigram + prefix match over full name, display name, email and title. */
  q: z.string().trim().min(1).max(100).optional(),
  /** Bulk resolve (≤ 100); cannot be combined with `q`. */
  ids: commaList(uuidSchema, { max: 100 }).optional(),
  status: z.enum(['active', 'deactivated', 'all']).default('active'),
  role: commaList(roleSchema).optional(),
  /** A department id, or `none`. */
  departmentId: z.union([uuidSchema, z.literal('none')]).optional(),
  /** A manager's user id, or `none` (people with no manager). */
  managerUserId: z.union([uuidSchema, z.literal('none')]).optional(),
  includeGuests: queryBooleanSchema.default(true),
  /** `human` leaves out bots and the Hub user: what the person pickers ask for (D-384). */
  kind: z.literal('human').optional(),
  sort: z.enum(['name', 'joined_desc']).default('name'),
});
export type ListMembersQuery = z.infer<typeof listMembersQuerySchema>;

export const memberListResponseSchema = paginated(memberDtoSchema);
export type MemberListResponse = z.infer<typeof memberListResponseSchema>;

/** `{ [fieldId]: value | null }` in profile updates. Values are validated by field type. */
export const customFieldsPatchSchema = z.record(uuidSchema, customFieldValueSchema);

/** PATCH /me/profile — department, manager and role stay admin-only. */
export const updateProfileRequestSchema = z.strictObject({
  fullName: personNameSchema.optional(),
  displayName: displayNameSchema.nullable().optional(),
  phone: phoneInputSchema.nullable().optional(),
  timezone: timeZoneInputSchema.optional(),
  /**
   * Your own job title (D-1250). It used to be admin-only, grouped with department, manager and
   * role — and those three are different in kind: they are the org's statement about where you
   * sit, and letting somebody edit them would let them edit the hierarchy. A job title is a
   * description of the work, it is what the directory and the people picker show beside a name,
   * and leaving it to an admin meant it was mostly empty.
   *
   * An admin can still set it on anybody (PATCH /members/:userId), which is the override that
   * makes this safe to open: a wrong title is visible to everyone and correctable by one person.
   */
  title: titleSchema.nullable().optional(),
  /** Only fields with editableBy = 'self'. */
  customFields: customFieldsPatchSchema.optional(),
});
export type UpdateProfileRequest = z.infer<typeof updateProfileRequestSchema>;

/** PATCH /members/:userId — admin+, D-006 rules for `role`. */
export const updateMemberRequestSchema = z.strictObject({
  role: roleSchema.optional(),
  /** Required when the new role is guest; cleared automatically for other roles. */
  guestScope: guestScopeSchema.optional(),
  departmentId: uuidSchema.nullable().optional(),
  managerUserId: uuidSchema.nullable().optional(),
  title: titleSchema.nullable().optional(),
  displayName: displayNameSchema.nullable().optional(),
  /** Any field, including editableBy = 'admins'. */
  customFields: customFieldsPatchSchema.optional(),
  /**
   * The payroll employee code (unique in the workspace; the payroll export needs one per line) and
   * the date of joining (the `min_tenure_days` pay gate). Never one's own employee code (D-930).
   */
  employeeCode: z
    .string()
    .trim()
    .min(1)
    .max(40)
    .regex(
      /^[A-Za-z0-9][A-Za-z0-9._/-]*$/,
      'Letters, digits, dots, dashes, slashes and underscores',
    )
    .nullable()
    .optional(),
  dateOfJoining: localDateSchema.nullable().optional(),
  /**
   * Why the employee code or the date of joining changes. Kept with the request when the person is
   * on a pay plan and a second person must approve it (D-099, D-957); ignored otherwise.
   */
  employmentChangeReason: multiLineText(1, 2000).optional(),
});
export type UpdateMemberRequest = z.infer<typeof updateMemberRequestSchema>;

// ---------------------------------------------------------------- pending member-detail changes (D-099)

/**
 * One request to change a person's payroll employee code or date of joining (D-957). For a person
 * on a pay plan `PATCH /members/:userId` does not write the change: it stores this request, and a
 * second person (an `hr_compensation` holder or the Owner, never the requester or the person)
 * applies it by approving. `oldValue` / `newValue` are the code as text, or a `YYYY-MM-DD` date;
 * `null` is "not set". Neither is an amount.
 */
export const memberDetailChangeDtoSchema = z.object({
  id: uuidSchema,
  userId: uuidSchema,
  kind: memberDetailChangeKindSchema,
  oldValue: z.string().nullable(),
  newValue: z.string().nullable(),
  /** The requester's reason, when they gave one. */
  reason: z.string().nullable(),
  status: memberDetailChangeStatusSchema,
  requestedByUserId: uuidSchema,
  requestedAt: isoDateTimeSchema,
  decidedByUserId: uuidSchema.nullable(),
  decidedAt: isoDateTimeSchema.nullable(),
  decisionNote: z.string().nullable(),
});
export type MemberDetailChangeDto = z.infer<typeof memberDetailChangeDtoSchema>;

export const memberDetailChangeIdParamsSchema = z.object({ changeId: uuidSchema });

/** `GET /members/pending-changes`. */
export const listMemberDetailChangesQuerySchema = paginationQuerySchema.extend({
  status: z.union([memberDetailChangeStatusSchema, z.literal('all')]).default('pending'),
  userId: uuidSchema.optional(),
});
export type ListMemberDetailChangesQuery = z.infer<typeof listMemberDetailChangesQuerySchema>;
export const memberDetailChangeListResponseSchema = paginated(memberDetailChangeDtoSchema);
export const memberDetailChangeResponseSchema = z.object({ change: memberDetailChangeDtoSchema });

/** Approve or reject a request (an `hr_compensation` holder or the Owner; not the requester or the person). */
export const decideMemberDetailChangeRequestSchema = z
  .strictObject({ note: multiLineText(1, 2000).optional() })
  .nullish();

/** `PATCH /members/:userId`: the member, plus the requests of this person that wait for approval. */
export const updateMemberResponseSchema = memberDtoSchema.extend({
  pendingChanges: z.array(memberDetailChangeDtoSchema),
});
export type UpdateMemberResponse = z.infer<typeof updateMemberResponseSchema>;

/** POST /members/:userId/deactivate. */
export const deactivateMemberRequestSchema = z.strictObject({
  /** Stored in the audit metadata. */
  reason: multiLineText(1, 500).optional(),
});
export type DeactivateMemberRequest = z.infer<typeof deactivateMemberRequestSchema>;

// ================================================================ departments

export const departmentDtoSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  description: z.string().nullable(),
  /** Active members. */
  memberCount: z.number().int().nonnegative(),
  archivedAt: isoDateTimeSchema.nullable(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});
export type DepartmentDto = z.infer<typeof departmentDtoSchema>;

export const departmentIdParamsSchema = z.object({ departmentId: uuidSchema });

export const listDepartmentsQuerySchema = z.object({
  /** `true` needs admin+. */
  includeArchived: queryBooleanSchema.default(false),
  q: z.string().trim().min(1).max(80).optional(),
});
export type ListDepartmentsQuery = z.infer<typeof listDepartmentsQuerySchema>;

export const departmentListResponseSchema = boundedList(departmentDtoSchema);

/** POST /departments — admin+ (`departments.manage`). */
export const createDepartmentRequestSchema = z.strictObject({
  /** Unique case-insensitively in the workspace, including archived departments. */
  name: singleLineText(1, 80),
  description: multiLineText(0, 500).nullable().optional(),
});
export type CreateDepartmentRequest = z.infer<typeof createDepartmentRequestSchema>;

/** PATCH /departments/:departmentId. */
export const updateDepartmentRequestSchema = z.strictObject({
  name: singleLineText(1, 80).optional(),
  description: multiLineText(0, 500).nullable().optional(),
  /** Archiving needs no active members; unarchiving is always allowed. */
  archived: z.boolean().optional(),
});
export type UpdateDepartmentRequest = z.infer<typeof updateDepartmentRequestSchema>;

// ================================================================ profile field definitions

export const profileFieldOptionSchema = z.object({ id: z.string(), label: z.string() });

export const profileFieldDtoSchema = z.object({
  id: uuidSchema,
  label: z.string(),
  type: profileFieldTypeSchema,
  /** [] unless select / multi_select. */
  options: z.array(profileFieldOptionSchema),
  visibility: profileFieldVisibilitySchema,
  editableBy: profileFieldEditorSchema,
  position: z.number().int(),
  archivedAt: isoDateTimeSchema.nullable(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});
export type ProfileFieldDto = z.infer<typeof profileFieldDtoSchema>;

export const profileFieldIdParamsSchema = z.object({ fieldId: uuidSchema });

export const listProfileFieldsQuerySchema = z.object({
  /** Needs admin+. */
  includeArchived: queryBooleanSchema.default(false),
});

export const profileFieldListResponseSchema = boundedList(profileFieldDtoSchema);

const optionLabelSchema = singleLineText(1, 60);

/** POST /profile-fields — admin+ (`profile_fields.manage`). */
export const createProfileFieldRequestSchema = z.strictObject({
  label: singleLineText(1, 60),
  type: profileFieldTypeSchema,
  /** Required for select / multi_select (1–50, unique labels), rejected otherwise. */
  options: z
    .array(z.strictObject({ label: optionLabelSchema }))
    .min(1)
    .max(50)
    .optional(),
  visibility: profileFieldVisibilitySchema.default('everyone'),
  editableBy: profileFieldEditorSchema.default('self'),
});
export type CreateProfileFieldRequest = z.infer<typeof createProfileFieldRequestSchema>;

/** PATCH /profile-fields/:fieldId — `type` cannot change. */
export const updateProfileFieldRequestSchema = z.strictObject({
  label: singleLineText(1, 60).optional(),
  /** Full replacement list; items with an `id` keep it. Dropping one needs confirmOptionRemoval. */
  options: z
    .array(z.strictObject({ id: z.string().min(1).max(64).optional(), label: optionLabelSchema }))
    .min(1)
    .max(50)
    .optional(),
  confirmOptionRemoval: z.boolean().optional(),
  visibility: profileFieldVisibilitySchema.optional(),
  editableBy: profileFieldEditorSchema.optional(),
  archived: z.boolean().optional(),
});
export type UpdateProfileFieldRequest = z.infer<typeof updateProfileFieldRequestSchema>;

/** PUT /profile-fields/order — exactly the set of non-archived field ids. */
export const reorderProfileFieldsRequestSchema = z.strictObject({
  fieldIds: z.array(uuidSchema).max(30),
});
export const reorderProfileFieldsResponseSchema = z.object({
  items: z.array(profileFieldDtoSchema),
});

// ================================================================ invites (D-022)

export const inviteDtoSchema = z.object({
  id: uuidSchema,
  email: emailOutSchema,
  role: roleSchema,
  guestScope: guestScopeSchema.nullable(),
  /** Channels a guest joins on acceptance (D-151); `[]` for other roles. */
  guestConversationIds: z.array(uuidSchema),
  departmentId: uuidSchema.nullable(),
  managerUserId: uuidSchema.nullable(),
  title: z.string().nullable(),
  /** pending + expiresAt < now is reported as expired. */
  status: inviteStatusSchema,
  invitedBy: z.object({ userId: uuidSchema, fullName: z.string() }),
  createdAt: isoDateTimeSchema,
  lastSentAt: isoDateTimeSchema,
  sendCount: z.number().int(),
  /** lastSentAt + 7 days (D-022). */
  expiresAt: isoDateTimeSchema,
  acceptedAt: isoDateTimeSchema.nullable(),
  acceptedUserId: uuidSchema.nullable(),
  revokedAt: isoDateTimeSchema.nullable(),
  revokedByUserId: uuidSchema.nullable(),
  emailDelivery: z.object({ status: emailDeliveryStatusSchema, lastError: z.string().nullable() }),
});
export type InviteDto = z.infer<typeof inviteDtoSchema>;

export const inviteIdParamsSchema = z.object({ inviteId: uuidSchema });

/**
 * The channels a guest invite names (PLAN §4 `POST /invites`, D-151): exactly one for
 * `single_channel`, one or more for `multi_channel`; public or private channels the inviter is in.
 */
export const guestConversationIdsSchema = z
  .array(uuidSchema)
  .min(1)
  .max(20)
  .refine((ids) => new Set(ids).size === ids.length, 'Each channel may be listed once.');

const invitePresetFields = {
  role: roleSchema,
  /** Required when role = guest, rejected otherwise. */
  guestScope: guestScopeSchema.optional(),
  /** Required when role = guest (1 for single_channel), rejected otherwise (D-151). */
  guestConversationIds: guestConversationIdsSchema.optional(),
  departmentId: uuidSchema.nullable().optional(),
  managerUserId: uuidSchema.nullable().optional(),
  title: titleSchema.nullable().optional(),
};

/** POST /invites — `invites.create`; role limits per `grantableInviteRoles` (D-006). */
export const createInviteRequestSchema = z.strictObject({
  email: emailSchema,
  ...invitePresetFields,
  personalMessage: multiLineText(1, 500).optional(),
});
export type CreateInviteRequest = z.infer<typeof createInviteRequestSchema>;

/** 201 (new) or 200 (rotated) from POST /invites; 200 from resend. */
export const inviteMutationResponseSchema = z.object({
  invite: inviteDtoSchema,
  /** The raw link; only ever returned here, so an admin can copy it if email failed (D-009). */
  inviteUrl: z.string(),
  emailDelivery: emailDeliveryStatusSchema,
});
export type InviteMutationResponse = z.infer<typeof inviteMutationResponseSchema>;

/**
 * One bulk row as the server checks it (after merging the defaults the handler applies). A row
 * that fails this schema becomes `outcome: 'error'` with `validation_failed`; it never fails the
 * batch (PLAN §4 "Row problems never fail the whole batch").
 */
export const bulkInviteRowSchema = z.strictObject({
  email: emailSchema,
  role: roleSchema.optional(),
  guestScope: guestScopeSchema.optional(),
  departmentId: uuidSchema.nullable().optional(),
  managerUserId: uuidSchema.nullable().optional(),
  title: titleSchema.nullable().optional(),
});
export type BulkInviteRow = z.infer<typeof bulkInviteRowSchema>;

/** Loose per-field types on the wire, bounded in size, so one bad row is only a row error. */
const looseRowText = z.string().max(400);

/** POST /invites/bulk — up to 100 rows, each processed independently. */
export const bulkInviteRequestSchema = z.strictObject({
  rows: z
    .array(
      z.strictObject({
        email: z.string().trim().max(400),
        role: looseRowText.optional(),
        guestScope: looseRowText.optional(),
        departmentId: looseRowText.nullable().optional(),
        managerUserId: looseRowText.nullable().optional(),
        title: looseRowText.nullable().optional(),
      }),
    )
    .min(1)
    .max(100),
  /** Merged under each row; `role` is required after the merge. */
  defaults: z
    .strictObject({
      role: roleSchema.optional(),
      guestScope: guestScopeSchema.optional(),
      /** The channels of every row that ends up as a guest (rows cannot name channels, D-151). */
      guestConversationIds: guestConversationIdsSchema.optional(),
      departmentId: uuidSchema.nullable().optional(),
      managerUserId: uuidSchema.nullable().optional(),
      title: titleSchema.nullable().optional(),
    })
    .optional(),
  personalMessage: multiLineText(1, 500).optional(),
  /** Validate and report without creating or sending anything. */
  dryRun: z.boolean().default(false),
});
export type BulkInviteRequest = z.infer<typeof bulkInviteRequestSchema>;

export const BULK_INVITE_OUTCOMES = [
  'created',
  'rotated',
  'skipped_already_member',
  'skipped_duplicate_row',
  'error',
] as const;

export const bulkInviteResponseSchema = z.object({
  dryRun: z.boolean(),
  results: z.array(
    z.object({
      index: z.number().int(),
      email: z.string(),
      outcome: z.enum(BULK_INVITE_OUTCOMES),
      invite: inviteDtoSchema.nullable(),
      inviteUrl: z.string().nullable(),
      emailDelivery: emailDeliveryStatusSchema.nullable(),
      error: z.object({ code: z.string(), message: z.string() }).nullable(),
    }),
  ),
  summary: z.object({
    created: z.number().int(),
    rotated: z.number().int(),
    skipped: z.number().int(),
    errors: z.number().int(),
  }),
});
export type BulkInviteResponse = z.infer<typeof bulkInviteResponseSchema>;

/** GET /invites — `invites.manage`; managers see only invites they sent. */
export const listInvitesQuerySchema = paginationQuerySchema.extend({
  /** Default `pending`. */
  status: commaList(z.enum(INVITE_STATUSES)).default(['pending']),
  q: z.string().trim().min(1).max(100).optional(),
  invitedByUserId: uuidSchema.optional(),
});
export type ListInvitesQuery = z.infer<typeof listInvitesQuerySchema>;

export const inviteListResponseSchema = paginated(inviteDtoSchema);

export const revokeInviteResponseSchema = z.object({ invite: inviteDtoSchema });

/** POST /invites/lookup — public. The token travels in the body, never in a URL. */
export const inviteLookupRequestSchema = z.strictObject({ token: opaqueTokenSchema });

export const inviteLookupResponseSchema = z.object({
  invite: z.object({
    email: emailOutSchema,
    role: roleSchema,
    guestScope: guestScopeSchema.nullable(),
    title: z.string().nullable(),
    departmentName: z.string().nullable(),
    expiresAt: isoDateTimeSchema,
  }),
  workspace: z.object({ id: uuidSchema, name: z.string() }),
  invitedBy: z.object({ fullName: z.string() }),
  /** Whether to show "Create your account" or "Sign in to join". */
  accountExists: z.boolean(),
});
export type InviteLookupResponse = z.infer<typeof inviteLookupResponseSchema>;

/** POST /invites/accept — new user; responds 201 `AuthSuccess` (see ./auth). */
export const acceptInviteRequestSchema = z.strictObject({
  token: opaqueTokenSchema,
  fullName: personNameSchema,
  /** Policy enforced (`422 password_policy_violation`). */
  password: passwordSchema,
  clientType: clientTypeSchema.default('web'),
  deviceName: deviceNameSchema.optional(),
});
export type AcceptInviteRequest = z.infer<typeof acceptInviteRequestSchema>;

/** POST /invites/accept-existing — Bearer; responds 200 `AuthSuccess` for a new session. */
export const acceptExistingInviteRequestSchema = z.strictObject({ token: opaqueTokenSchema });
export type AcceptExistingInviteRequest = z.infer<typeof acceptExistingInviteRequestSchema>;

// ================================================================ avatars (D-010)

/** The optional `crop` multipart part (JSON): a square in source pixels. */
export const avatarCropSchema = z.strictObject({
  x: z.number().int().nonnegative(),
  y: z.number().int().nonnegative(),
  size: z.number().int().min(64),
});
export type AvatarCrop = z.infer<typeof avatarCropSchema>;

/** PUT /me/avatar, PUT /members/:userId/avatar. */
export const avatarResponseSchema = z.object({ avatar: avatarDtoSchema });

// ================================================================ local signed file URLs (D-010)

/** GET /files/local/* query: `sig` = hex HMAC-SHA256 of `${method}\n${key}\n${exp}\n${contentType}`. */
export const localFileQuerySchema = z.object({
  exp: z.coerce.number().int().positive(),
  sig: z.string().regex(/^[0-9a-f]{64}$/),
  disposition: z.enum(['inline', 'attachment']).optional(),
});
export type LocalFileQuery = z.infer<typeof localFileQuerySchema>;

// ================================================================ audit log (D-021)

export const auditEventDtoSchema = z.object({
  id: uuidSchema,
  /** e.g. "member.role_changed". */
  action: z.string(),
  actor: z.object({
    kind: z.enum(AUDIT_ACTOR_KINDS),
    userId: uuidSchema.nullable(),
    displayName: z.string().nullable(),
  }),
  target: z.object({ type: z.string(), id: uuidSchema.nullable(), label: z.string().nullable() }),
  /** e.g. { from: "member", to: "manager" }. */
  metadata: z.record(z.string(), z.unknown()),
  ipAddress: z.string().nullable(),
  userAgent: z.string().nullable(),
  createdAt: isoDateTimeSchema,
});
export type AuditEventDto = z.infer<typeof auditEventDtoSchema>;

/** GET /admin/audit-events — admin+ (`audit.read`). Sorted id DESC. */
export const listAuditEventsQuerySchema = paginationQuerySchema.extend({
  /** Exact names or a `prefix.*` wildcard, e.g. `member.*`. */
  action: commaList(z.string().regex(/^[a-z][a-z_]*(\.[a-z][a-z_]*)*(\.\*)?$/)).optional(),
  actorUserId: uuidSchema.optional(),
  targetType: auditTargetTypeSchema.optional(),
  targetId: uuidSchema.optional(),
  /** Inclusive lower bound. */
  from: isoDateTimeSchema.optional(),
  /** Exclusive upper bound. */
  to: isoDateTimeSchema.optional(),
});
export type ListAuditEventsQuery = z.infer<typeof listAuditEventsQuerySchema>;

export const auditEventListResponseSchema = paginated(auditEventDtoSchema);

// ================================================================ dev mailbox (D-009)

/** GET /dev/mailbox — only when NODE_ENV !== 'production' and DEV_MAILBOX=1. */
export const devMailboxQuerySchema = z.object({
  to: emailSchema.optional(),
  kind: mailKindSchema.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type DevMailboxQuery = z.infer<typeof devMailboxQuerySchema>;

export const devMailboxMessageSchema = z.object({
  id: z.string(),
  to: z.array(z.string()),
  subject: z.string(),
  text: z.string(),
  html: z.string().nullable(),
  kind: z.enum(MAIL_KINDS),
  /** Filled by the template, so tests read the OTP code or invite link without parsing HTML. */
  meta: z.object({ code: z.string().nullable(), url: z.string().nullable() }),
  sentAt: isoDateTimeSchema,
});
export type DevMailboxMessage = z.infer<typeof devMailboxMessageSchema>;

export const devMailboxResponseSchema = boundedList(devMailboxMessageSchema);
